import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { Observable } from 'rxjs';
import { UserData } from '../data-models/user-models/user-data.model';
import { SharedMethodsService } from './shared-methods.service';
import { CommonDataService } from './storage/common-data.service';
import { FisheryNoticesService } from './storage/fishery-notices.service';
import { UserStorageService } from './storage/user-storage.service';
import { environment } from '../../environments/environment';



@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  // promise resolve
  private resolve: boolean = false;


  constructor(
    private http: HttpClient,
    private router: Router,
    private onlineStatusService: OnlineStatusService,
    private sharedMethodService: SharedMethodsService
  ) {

  }


  /* ---------- User Details Methods ---------- */


  // get all necessary data for a component
  public getUserDetails(): Promise<boolean> {
    this.resolve = false;
    return new Promise<boolean>((res) => {
      if (this.onlineStatusService.getStatus() === OnlineStatusType.OFFLINE) {
        this.checkForUserOffline().then(() => {
          res(this.resolve);
        });
      }

      else {
        this.checkForUserOnline().then(() => {
          res(this.resolve);
        });
      }
    });

  }

  // get data given that the user is offline
  checkForUserOffline(): Promise<boolean> {

    return new Promise<boolean>((res) => {
      let currUsername: string = this.getCurrUsername();
      if (currUsername) {
        // get user data with the username saved in local storage
        UserStorageService.getUserOffline(currUsername).then(() => {
          this.getUserData().then(() => {
            res(this.resolve);
          });
        });
      }
      else {
        this.dataError("Username not saved to storage");
        res(false);
      }
    });

  }

  // get data given that the user is online
  checkForUserOnline(): Promise<boolean> {

  // Check if user is logged in, then get their metadata
    return new Promise<boolean>((res) => {
      let currentLogin = AuthenticationService.getCurrentLogin();
      if (currentLogin != '') {
        UserStorageService.getUserOnline(currentLogin).then(() => {
          this.getUserData().then(() => {
            res(this.resolve);
          });
        });
      } else {
        this.dataError("Not authenticated");
        res(false);
      }
    });

  }

  // get the current user data
  getUserData(): Promise<boolean> {

    return new Promise<boolean>((res) => {
      if (this.dataError(UserStorageService.error)) {
        res(false);
      }
      else {
        let currUser: UserData = UserStorageService.currUser;
        if ((!currUser || !currUser.userEmail) && !UserStorageService.adminUser) {
          this.dataError("User ID or Email not defined");
          res(false);
        }
        else {
          res(this.resolve = true);
        }
      }
    });

  }

  public getUserMetaData(email: string): Observable<any> {

    email = email.replace(/\+/g, "%2b");
    const url = `${environment.getCouchUser}&username=${email}`;
    return this.http.get<any>(url);
                      
  } 


  /* ---------- Login Methods ---------- */


  public postLogin(email: string, password: string): Observable<any> {

    const url = `${environment.databaseUrl}/_session`
    return this.http.post<any>(url, { 'name': email, 'password': password }, { headers: { 'Content-Type': 'application/json' } });

  }

  public static setLogin(username: string): void {

    this.saveData('logged_in', username);

  }


  public static getCurrentLogin(): string {

    return this.getData('logged_in') ?? '';

  }


  /* ---------- Sign-Up Methods ---------- */


  public postSignUp(user: UserData, password: string): Observable<any> {

    let postData: {name: string, password: string, roles: string[], type: string, user: UserData} = {
      name: user.userEmail,
      password: password,
      roles: [],
      type: 'user',
      user: user
    };

    
    let username: string = postData.name;
    username = username.replace(/\+/g, "%2b");
    const url = `${environment.postCouchUser}&username=${username}`;
    return this.http.post<any>(url, postData);
  
  }


  /* ---------- Password Change Method ---------- */



  public postPasswordChange(email: string, token: string, password: string, hash: string): Observable<any> {

    email = email.replace(/\+/g, "%2b");
    const url = `${environment.changePassword}&username=${email}&token=${token}&password=${password}&hash=${hash}`;
    return this.http.get<any>(url);

  }


  /* ---------- Log Out Methods ---------- */


  // log out of account and go back to login screen
  public logout(): void {

    // close all databases
    UserStorageService.closeStorage(UserStorageService.adminUser != undefined);
    CommonDataService.closeCommonDb(UserStorageService.adminUser != undefined);
    FisheryNoticesService.closeNoticeDb(UserStorageService.adminUser != undefined);
    UserStorageService.adminUser = undefined;
    this.router.navigate(['login']);

  }


  /* ---------- Username Methods ---------- */

  
  // save the current username to local storage
  public static saveCurrUsername(username: string): void {

    this.saveData('username', username);

  }
  
  // get local storage data from key 'username'
  getCurrUsername(): string {

    return AuthenticationService.getData('username');

  }

  // add a new username to the username list
  public static addNewUsername(username: string): void {

    let allUsernames: string[] = this.getAllUsernames();
    allUsernames.push(username);
    this.saveData('allUsernames', allUsernames);

  }

  // get all of the current usernames
  public static getAllUsernames(): string[] {

    return SharedMethodsService.getStringList(this.getData('allUsernames'));

  }


  /* ---------- Offline Auth Methods ---------- */


  // save data to local storage through [key, value] pair
  private static saveData(key: string, value: any): void {

    localStorage.setItem(key, JSON.stringify(value));

  }

  // get local storage data from key
  private static getData(key: string): any {

    let item: string | null = localStorage.getItem(key);
    if (item) {
      return JSON.parse(item);
    }
    return null;

  }


  /* ---------- Helper Methods ---------- */


  // if a user data error occurs, log out of the app
  public dataError(error: string): boolean {

    if (error) {
      this.sharedMethodService.toastDataRetrievalError(error);
      
      return true;
    }
    return false;

  }

}
